// Малый редактор для форматированных фрагментов вне основного текста: задание, реферат, раздел ИИ (абзацы и
// перечисления; на листах задания/реферата - ещё рисунки, таблицы, формулы, листинги из загруженного Word, objects),
// текст сноски (одна строка - mode="inline"). Схема - подмножество основной.
// Оформление и клавиатура - как в основном тексте (и как в DOCX): абзацный отступ, по ширине, Times New Roman,
// Shift+Enter - новая строка, «- »/«а) » - перечисление, Tab - уровень… Вставка - без неразрывных пробелов (USE_NBSP)
// (core/editor/wordKeys.ts). Строка состояния показывает тип текущей строки (core/editor/lineKind.ts).
import { useEffect, useRef } from 'react';
import { EditorContent, useEditor, type JSONContent } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import Code from '@tiptap/extension-code';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Link from '@tiptap/extension-link';
import HardBreak from '@tiptap/extension-hard-break';
import { UndoRedo, Placeholder } from '@tiptap/extensions';
import { StoParagraph } from '../schema/base';
import { StoList, StoListItem } from '../schema/lists';
import { MathInline, Formula } from '../schema/math';
import { sheetObjectExtensions } from '../schema/objects';
import { Extension, InputRule, type AnyExtension } from '@tiptap/core';
import { findWrapping } from '@tiptap/pm/transform';
import { setActiveMini, miniTick } from './active';
import { ListCaseExtension } from './listCase';
import { wordKeys } from './wordKeys';
import { plainSlice } from './paste';
import { setLineFocus } from './lineKind';

// разрыв строки - Shift+Enter (wordKeys); Ctrl+Enter в Word - разрыв страницы, в листе задания/реферата его нет
const MiniHardBreak = HardBreak.extend({ addKeyboardShortcuts() { return {}; } });

// абзацы малого редактора без id: id нужны только узлам основного текста (иначе при разбиении абзаца id дублируются)
const MiniParagraph = StoParagraph.extend({ addAttributes() { return {}; } });

/** «- », «– », «а) », «1) » в начале абзаца - перечисление (как в основном тексте). */
const MiniListRules = Extension.create({
  name: 'miniListRules',
  addInputRules() {
    const schema = this.editor.schema;
    if (!schema.nodes.stoList) return [];
    const rule = (find: RegExp, kind: string) => new InputRule({
      find,
      handler: ({ state, range }) => {
        const $s0 = state.doc.resolve(range.from);
        if ($s0.parent.type.name !== 'paragraph' || $s0.depth !== 1) return null;
        const tr = state.tr.delete(range.from, range.to);
        const r = tr.doc.resolve(range.from).blockRange();
        const wrap = r && findWrapping(r, schema.nodes.stoList, { kind });
        if (!r || !wrap) return null;
        tr.wrap(r, wrap);
        return undefined;
      },
    });
    // «1) » на верхнем уровне - «а)» (4.4.4: цифры со скобкой - только для вложенного перечисления)
    return [rule(/^\s*[-–—*]\s$/, 'dash'), rule(/^\s*[аa]\)\s$/, 'letter'), rule(/^\s*1\)\s$/, 'letter')];
  },
});

export function MiniEditor({ value, onChange, mode = 'block', placeholder, testid, minHeight = 80, paper = true, where = '', extensions = [], objects = false }: {
  value: JSONContent | JSONContent[] | null | undefined;
  onChange: (v: any) => void;
  /** block: значение - документ {type:'doc'}; inline: значение - массив строчных узлов (содержимое одного абзаца) */
  mode?: 'block' | 'inline';
  placeholder?: string;
  testid?: string;
  minHeight?: number;
  /** рамка «куска листа» (в окнах); false - текст прямо на листе (задание, реферат) */
  paper?: boolean;
  /** название места для строки состояния («Задание», «Реферат»…) */
  where?: string;
  /** дополнительные расширения (лист перед текстом: неразрывные пробелы как в DOCX, перенос на следующий лист) */
  extensions?: AnyExtension[];
  /** лист «Задание»/«Реферат»: ещё рисунки, таблицы, формулы, листинги (из загруженного Word - схема цепи, таблица
   *  исходных данных); без них такой лист при первой правке потерял бы объекты */
  objects?: boolean;
}) {
  const inline = mode === 'inline';
  const editorRef = useRef<any>(null);
  const initial: JSONContent = inline
    ? { type: 'doc', content: [{ type: 'paragraph', content: (value as JSONContent[]) || [] }] }
    : ((value as JSONContent) && (value as JSONContent).type === 'doc' ? (value as JSONContent) : { type: 'doc', content: [{ type: 'paragraph' }] });
  const editor = useEditor({
    extensions: [
      inline ? Document.extend({ content: 'paragraph' })
        : Document.extend({ content: objects ? '(paragraph | stoList | figure | stoTable | formula | listing)+' : '(paragraph | stoList)+' }),
      MiniParagraph, Text, MiniHardBreak, Bold, Italic, Underline, Code, Subscript, Superscript, MathInline,
      ...(objects && !inline ? [...sheetObjectExtensions(), Formula] : []),
      Link.configure({ openOnClick: false, autolink: true }),
      // позиция перечисления: Enter / Backspace / Tab - раньше общих клавиш ядра TipTap (как в основном тексте)
      ...(inline ? [] : [StoList.extend({ content: 'stoListItem+' }), StoListItem.extend({ priority: 150 }), MiniListRules, ListCaseExtension]),
      UndoRedo, Placeholder.configure({ placeholder: placeholder || '' }), ...wordKeys(), ...extensions,
    ],
    content: initial,
    immediatelyRender: true,
    editorProps: {
      attributes: { class: 'mini-editor sto-doc' + (inline ? ' is-inline' : ''), lang: 'ru', spellcheck: 'true', ...(testid ? { 'data-testid': testid } : {}) },
      // неразрывные пробелы и дефисы из буфера - обычными (в DOCX их нет - core/schema/constants USE_NBSP)
      transformPasted: plainSlice,
      // команды ленты и сочетания клавиш (перечисления, полужирный…) - в этот редактор, пока курсор в нём
      handleDOMEvents: {
        focus: () => { setActiveMini(editorRef.current); setLineFocus(editorRef.current, where || (inline ? 'Сноска' : 'Текст')); return false; },
        blur: () => { setLineFocus(null, ''); return false; },
      },
    },
    onSelectionUpdate: ({ editor: ed }) => { if (ed.view.hasFocus()) setLineFocus(ed as any, where || (inline ? 'Сноска' : 'Текст')); },
    onTransaction: ({ editor: ed }) => { if (ed.view.hasFocus()) miniTick(); },
    onUpdate: ({ editor: ed }) => {
      const j = ed.getJSON();
      onChange(inline ? (j.content?.[0]?.content || []) : j);
    },
  });
  editorRef.current = editor;
  useEffect(() => () => { setActiveMini(null); setLineFocus(null, ''); editor?.destroy(); }, [editor]);
  return <div className={'mini-editor-wrap' + (paper ? ' is-paper' : '')} style={{ minHeight }}><EditorContent editor={editor} /></div>;
}
